import { useCallback, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Status } from '../../../../../shared/builds/types';
import Locator from '../../../../../shared/Locator';
import List from '../../../generic/List';
import DependencyActionDropdown from '../../dependenciesV2/DependenciesTable/DependencyTableRow/DependencyActionDropdown';
import { DetailsRow } from '../../dependenciesV2/DependenciesTable/DependencyTableRow/DetailsRow';
import { getStatusFromDependencyState } from '../../dependenciesV2/DependenciesTable/DependencyTableRow/helpers';
import StateContextPanel from '../../dependenciesV2/DependenciesTable/DependencyTableRow/StateContextPanel';
import type { DependencyForAPI } from '../../../../../shared/dependenciesV2/response';
import type { Fetcher } from '../../../../../shared/fetchers';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../typings/api';
import type { DependencyToRemove } from '../../dependenciesV2/modals/RemoveDependency';

import VendoredDependencyRowTitle from './VendoredDependencyRowTitle';

import styles from './VendoredDependencyRow.module.less';

// import styles from './VendoredDependencyRow.module.less';

type Props = {
  dependency: DependencyForAPI;
  setSelectedDependencyRevisionLocator?: (locator: string) => void;
  setDependencyToRemove: (depedency: DependencyToRemove | null) => void;
  setDependencyLocatorToOverwrite: (locator: string | null) => void;
  setManualDependencyAddMode: (mode: ManuallyAddedDependencyRequestKind | null) => void;
  setInProgressBuildId: (buildId: number | null) => void;
};

/**
 * Renders a row for a vendored dependency.
 */
const VendoredDependencyRow: VoidFunctionComponent<Props> = ({
  dependency,
  setSelectedDependencyRevisionLocator,
  setDependencyToRemove,
  setDependencyLocatorToOverwrite,
  setManualDependencyAddMode,
  setInProgressBuildId,
}) => {
  //  Parse the locator to extract fetcher and package information,
  //  with reasonable defaults.
  const { fetcher, packageName, version, locatorFull } = useMemo(() => {
    const loc = new Locator(dependency.locator);

    const locFetcher = (loc.fetcher.value || 'unknown') as Fetcher;
    const locPackage = dependency.title || loc.package.value || ''; //
    const locVersion = dependency.version || loc.revision.value || '';
    return {
      fetcher: locFetcher,
      packageName: locPackage,
      version: locVersion,
      locatorFull: loc.toFullString(),
    };
  }, [dependency.locator, dependency.title, dependency.version]);

  // Get the status of the dependency
  const status = getStatusFromDependencyState(dependency.status);

  const onClick = useCallback(() => {
    if (setSelectedDependencyRevisionLocator) {
      setSelectedDependencyRevisionLocator(dependency.locator);
    }
  }, [setSelectedDependencyRevisionLocator, dependency.locator]);
  return (
    <List.RowContainer>
      <List.Row
        hasTightPadding
        onClick={onClick}
      >
        <div className={styles.dependencyRow}>
          <div>
            <VendoredDependencyRowTitle
              depth={dependency.depth}
              fetcher={fetcher}
              packageName={packageName}
              version={version}
              locatorFull={locatorFull}
            />
            {status === Status.Analyzed ? (
              <DetailsRow dependency={dependency} isReleaseGroupScope={false} />
            ) : (
              <div className={styles.statusPanel}>
                <StateContextPanel
                  locator={dependency.locator}
                  status={dependency.status}
                  setInProgressBuildId={setInProgressBuildId}
                />
              </div>
            )}
          </div>
          <div className={styles.actionDropdown}>
            <DependencyActionDropdown
              dependency={dependency}
              status={status}
              setDependencyToRemove={setDependencyToRemove}
              setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
              setManualDependencyAddMode={setManualDependencyAddMode}
              setInProgressBuildId={setInProgressBuildId}
              isProjectScope // always in project scope for vendored deps for now
            />
          </div>
        </div>
      </List.Row>
    </List.RowContainer>
  );
};

export default VendoredDependencyRow;
